import { Theme, Components } from '@mui/material/styles';
import { MuiAutocomplete } from './components/MuiAutocomplete';
import { MuiChip } from './components/MuiChip';
import { MuiAlert } from './components/MuiAlert';
import { MuiDataGrid } from './components/MuiDataGrid';
import { MuiDialog } from './components/MuiDialog';
import { MuiDialogActions } from './components/MuiDialogActions';
import { MuiDialogContent } from './components/MuiDialogContent';
import { MuiDialogTitle } from './components/MuiDialogTitle';
import { MuiRadio } from './components/MuiRadio';
import { MuiSelect } from './components/MuiSelect';
import { MuiTextField } from './components/MuiTextField';
import { MuiList, MuiListItem } from './components/MuiList';

import {
  MuiPickersCalendarHeader,
  MuiDayCalendar,
  MuiDateRangePickerDay,
} from './components/MuiDateRangeCalendar';
import { palette } from './palette';
import { fontFamily, typography } from './typography';

import '@mui/x-data-grid-pro/themeAugmentation';
import '@mui/x-date-pickers-pro/themeAugmentation';
import { DEFAULT_BORDER_RADIUS } from './constants';
import { pxToRem } from 'src/common/utils/fonts';
import { MuiSwitch } from './components/MuiSwitch';
import { MuiIconButton } from './components/MuiIconButton';
import { MuiDateCalendar } from './components/MuiDateCalendar';
import { MuiPickersDay, MuiPickersToolbar } from './components/MuiPickers';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    tertiary: true;
    active: true;
  }
}

declare module '@mui/material/Checkbox' {
  interface CheckboxPropsColorOverrides {
    tertiary: true;
  }
}

declare module '@mui/material/LinearProgress' {
  interface LinearProgressPropsColorOverrides {
    grey: true;
  }
}

const borderRadius = DEFAULT_BORDER_RADIUS;
const buttonBorderRadius = borderRadius;

const primaryButtonStyle = {
  color: palette.text.white,
  backgroundColor: palette.grey[600],
  boxShadow: 'none',
  '&:hover': {
    background: palette.grey[500],
    boxShadow: `0px ${pxToRem(2)} ${pxToRem(4)} rgba(0, 0, 0, 0.12)`,
  },
};

const secondaryButtonStyle = {
  color: palette.text.secondary,
  backgroundColor: palette.text.white,
  boxShadow: `0px 0px ${pxToRem(12)} rgba(0, 0, 0, 0.12)`,
  '&:hover': {
    background: palette.grey[200],
    color: palette.text.primary,
    boxShadow: 'none',
  },
};

const tertiaryButtonStyle = {
  color: palette.grey[600],
  backgroundColor: palette.text.white,
  '&:hover': {
    background: palette.grey[200],
  },
};

const activeButtonStyle = {
  color: palette.text.secondary,
  background: palette.grey[200],
  boxShadow: 'none',
  '&:hover': {
    background: palette.grey[200],
    color: palette.text.secondary,
    boxShadow: 'none',
  },
};

export const components: Components<Theme> = {
  MuiCard: {
    styleOverrides: {
      root: ({ theme }) => {
        return {
          boxShadow: 'none',
          border: `1px solid ${theme.palette.border}`,
        };
      },
    },
  },
  MuiCssBaseline: {
    styleOverrides: {
      '@global': {
        body: {
          margin: 0,
          fontFamily,
        },
      },
    },
  },
  MuiCheckbox: {
    styleOverrides: {
      root: ({ ownerState }) => {
        let colorStyle = {};
        if (ownerState.color === 'primary') {
          colorStyle = primaryButtonStyle;
        }

        return {
          ...colorStyle,
          backgroundColor: 'none',
          '&:hover': { background: 'none', boxShadow: 'none' },
        };
      },
    },
  },
  MuiButton: {
    defaultProps: {
      variant: 'contained',
    },
    styleOverrides: {
      root: ({ ownerState, theme }) => {
        let colorStyle = {};
        if (ownerState.color === 'primary') {
          colorStyle = primaryButtonStyle;
        } else if (ownerState.color === 'secondary') {
          colorStyle = secondaryButtonStyle;
        } else if (ownerState.color === 'tertiary') {
          colorStyle = tertiaryButtonStyle;
        } else if (ownerState.color === 'active') {
          colorStyle = activeButtonStyle;
        }

        return {
          textTransform: 'none',
          borderRadius: buttonBorderRadius,
          ...typography.button,
          minHeight: theme.spacing(40),
          padding: theme.spacing(8, 16, 8, 16),
          ...colorStyle,
          '&.Mui-disabled': {
            color: palette.text.tertiary,
            backgroundColor: palette.border,
          },
        };
      },
    },
  },
  MuiTableRow: {
    defaultProps: {
      hover: true,
    },
    styleOverrides: {
      root: {
        fontFamily,
        '&.MuiTableRow-hover': {
          '&:hover': {
            backgroundColor: palette.background.default,
          },
        },
      },
      head: {
        fontFamily,
        whiteSpace: 'nowrap',
        backgroundColor: palette.grey['100'],
      },
    },
  },
  MuiTable: {
    styleOverrides: {
      root: {
        fontFamily,
        borderCollapse: 'separate',
        borderSpacing: 0,
        overflow: 'hidden',
        backgroundColor: palette.background.paper,
      },
    },
  },
  MuiTableHead: {
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily,
        borderRadius: 0,
        background: palette.grey['100'],
        padding: theme.spacing(8),
      }),
    },
  },
  MuiTableCell: {
    styleOverrides: {
      head: {
        borderRadius: 0,
        background: palette.grey['100'],
        borderBottom: `1px solid ${palette.grey['200']}`,
      },
      body: {
        borderBottom: `1px solid ${palette.border}`,
      },
      root: ({ theme }) => ({
        fontFamily,
        paddingLeft: theme.spacing(24),
        paddingRight: theme.spacing(24),
        whiteSpace: 'nowrap',
        borderColor: palette.background.default,
      }),
    },
  },
  MuiTablePagination: {
    defaultProps: {
      showFirstButton: true,
      showLastButton: true,
    },
    styleOverrides: {
      root: ({ theme }) => ({
        fontFamily,
        background: palette.grey['100'],
        borderRadius: `0px 0px ${theme.spacing(8)} ${theme.spacing(8)}`,
        borderTop: `1px solid ${palette.grey['200']}`,
      }),
      toolbar: ({ theme }) => ({
        display: 'grid',
        gridTemplateColumns: `${theme.spacing(120)} ${theme.spacing(100)} 1fr ${theme.spacing(
          220,
        )}`,
        paddingLeft: theme.spacing(12),
        paddinRight: theme.spacing(12),
        margin: 0,
        minHeight: `${theme.spacing(52)} !important`,
      }),
      selectLabel: ({ theme }) => ({
        ...typography.text1,
        margin: 0,
        marginLeft: theme.spacing(12),
        color: palette.text.secondary,
      }),
      spacer: {
        display: 'none',
      },
      displayedRows: {
        ...typography.text1,
        color: palette.text.primary,
        textAlign: 'center',
        margin: 0,
      },
      actions: {
        textAlign: 'right',
      },
    },
  },
  MuiMenu: {
    styleOverrides: {
      root: {
        fontFamily,
      },
      list: {
        backgroundColor: palette.text.white,
        padding: 0,
        borderRadius: `${DEFAULT_BORDER_RADIUS} !important`,
        overflow: 'hidden',
      },
      paper: ({ theme }) => ({
        borderRadius: `${DEFAULT_BORDER_RADIUS} !important`,
        boxShadow: `0px 0px ${theme.spacing(12)} rgba(0, 0, 0, 0.12)`,
        padding: theme.spacing(8),
      }),
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        minHeight: 32,
        fontFamily,
      },
      indicator: {
        display: 'none',
      },
    },
  },
  MuiTab: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...typography.h5,
        fontFamily,
        color: palette.text.secondary,
        textTransform: 'none',
        '&.Mui-selected': {
          ...typography.h4,
          color: palette.text.primary,
          backgroundColor: palette.text.white,
          textDecoration: 'none',

          borderTopLeftRadius: theme.spacing(8),
          borderTopRightRadius: theme.spacing(8),
          borderColor: palette.grey['300'],
          borderWidth: '1px 1px 0 1px',
          borderStyle: 'solid',
        },
      }),
    },
  },
  MuiListItemButton: {
    styleOverrides: {
      root: {
        fontFamily,
        '&.Mui-selected': {
          backgroundColor: palette.grey['200'],
          '&:hover': {
            backgroundColor: palette.grey['200'],
          },
        },
        '&:hover': {
          backgroundColor: palette.grey['200'],
        },
      },
    },
  },
  MuiDialog,
  MuiDialogTitle,
  MuiDialogContent,
  MuiDialogActions,
  MuiChip,
  MuiFormLabel: {
    styleOverrides: {
      root: {
        '&.Mui-focused': {
          color: palette.grey[600],
        },
      },
    },
  },
  MuiMenuItem: {
    styleOverrides: {
      root: ({ theme }) => ({
        padding: theme.spacing(6, 12),
        fontFamily,
        '&.Mui-selected': {
          background: palette.grey[200],
        },
        '&.Mui-selected:hover': {
          background: palette.grey[300],
        },
        '&:hover': {
          background: palette.grey[100],
        },
      }),
    },
  },

  MuiTextField: MuiTextField,
  MuiInputBase: {
    styleOverrides: {
      root: ({ theme }) => ({
        ...typography.text1,
        color: palette.text.primary,
        height: theme.spacing(40),
        borderRadius: buttonBorderRadius,
      }),
      input: {
        ...typography.text1,
        color: palette.text.primary,
        '&::placeholder': {
          ...typography.text1,
          color: palette.text.secondary,
          opacity: 1,
        },
      },
    },
  },
  MuiSelect: MuiSelect,
  MuiSwitch: MuiSwitch,
  MuiFormControl: {
    styleOverrides: {
      root: {
        fontFamily,
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        fontFamily,
      },
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        fontFamily,
        '& .MuiMultiSectionDigitalClockSection-root::after': {
          height: 0,
        },
      },
    },
  },

  MuiTooltip: {
    styleOverrides: {
      tooltip: ({ theme }) => ({
        backgroundColor: palette.text.white,
        color: palette.text.secondary,
        borderRadius: DEFAULT_BORDER_RADIUS,
        border: `1px solid  #E5E5E5`,
        padding: theme.spacing(1, 8, 0),
        ...typography.text5,
      }),
      tooltipPlacementTop: ({ theme }) => ({
        marginBottom: `${theme.spacing(4)} !important`,
      }),
    },
  },
  MuiList,
  MuiListItem,
  MuiListItemText: {
    defaultProps: {},
    styleOverrides: {
      root: {
        fontFamily,
        fontSize: '1rem',
        lineHeight: '1.5rem',
        fontWeight: 400,
      },
    },
  },
  MuiAlert,
  MuiDataGrid,
  MuiAutocomplete,
  MuiRadio,
  MuiPickersCalendarHeader,
  MuiDayCalendar,
  MuiDateRangePickerDay,
  MuiDateCalendar,
  MuiIconButton,
  MuiPickersDay,
  MuiPickersToolbar,
};
